import type { AnchorNames } from './useAnchor.js';

export type { AnchorNames };

/**
 * @aj/coach — guided flows over a live UI.
 *
 * Onboarding, feature introductions and one-off tips are the same mechanism:
 * a short sequence of steps pointing at things that are really on screen. One
 * primitive covers all three, which is what makes it worth building once.
 *
 * NOTHING IN THIS PACKAGE KNOWS WHAT THE HOST APP IS. That is the whole
 * design constraint, and every rule below follows from it:
 *
 *   - Anchors are `data-coach` attributes, never classes or refs. A styling
 *     refactor cannot break a flow, and the attribute is a greppable contract
 *     saying "this element is taught".
 *   - Copy arrives as already-translated strings. No message keys, no `t()`,
 *     no `Intl` — the same rule the design system follows.
 *   - Persistence is an injected port. This app stores flow state outside its
 *     event journal; another app can hand over `localStorage` in four lines.
 *   - The HOST decides *when* to offer a flow; the LIBRARY decides *whether*
 *     to run it now. Domain predicates stay outside; queueing, budgeting and
 *     seen-state live in exactly one place.
 *
 * The coach never writes the host's data. It points at the real control and
 * waits for the host to say the deed is done. That is what lets onboarding
 * run on the live UI rather than on a mock of it: the student learns the
 * button they will actually press tomorrow, and validation stays where it
 * already lives.
 */

/** A picture or a short clip beside the copy. See `Step.media`. */
export type Media = {
  kind: 'image' | 'video';
  src: string;
  /** Still frame shown before a video plays. Strongly recommended: without
   *  one, a slow network shows an empty box where the explanation should be. */
  poster?: string;
  /** Always required — a flow that only works for sighted users is a flow
   *  that fails the people most likely to need it. */
  alt: string;
};

/**
 * One pre-written answer.
 *
 * A bare string is the common case, where the words ARE the answer — "my
 * phone" is both what the student taps and what gets stored. Give the object
 * form when the answer has an identity of its own: the host gets back a
 * stable `value` instead of having to match translated copy back to a
 * meaning, which breaks the moment anyone rewords a chip or switches locale.
 */
export type Chip = string | { label: string; value: string };

export const chipLabel = (chip: Chip) => (typeof chip === 'string' ? chip : chip.label);
export const chipValue = (chip: Chip) => (typeof chip === 'string' ? chip : chip.value);

/**
 * A short answer collected mid-flow.
 *
 * Deliberately minimal. This is for the things that have no control of their
 * own anywhere else — "what got in the way on Wednesday?" — and NOT for the
 * host's real data entry, which belongs in the host's real forms where its
 * validation already is.
 */
export type Input = {
  /** Pre-written answers, offered as one tap each. The point of chips is that
   *  choosing is not the same work as composing: asked to write, people give
   *  up or write "laziness"; offered five options, they pick the true one. */
  chips?: Chip[];
  placeholder?: string;
  /** Free text may be refused, leaving chips as the only way through. */
  allowFreeText?: boolean;
  /** Skipping must always be possible. A flow that cannot be escaped is a
   *  trap, and the first thing a trapped user does is uninstall. */
  optional?: boolean;
};

/**
 * When a step gives way to the next one.
 *
 * `'next'` is a button the reader taps — right for a step that only explains.
 *
 * An action token is right for a step that ASKS FOR SOMETHING: "tap Add
 * habit" should not have a Next button, because Next lets you agree without
 * doing it, and then the flow teaches nothing. The token is an opaque string
 * to this package; the host calls `coach.completed('habit.created')` when its
 * own code has done the deed.
 */
export type Advance = 'next' | { on: string };

type StepBase = {
  /** Already translated. Keep it to a sentence — this is a caption, not a
   *  paragraph, and nobody reads a paragraph floating over their screen. */
  body: string;
  title?: string;
  advance?: Advance;
  /** Seconds to wait for a missing anchor before giving up on this step.
   *  Defaults to `ANCHOR_TIMEOUT_MS`. */
  waitMs?: number;
};

export type Step = StepBase &
  (
    | {
        kind: 'spotlight';
        /**
         * Matches `[data-coach="<anchor>"]`.
         *
         * Give several and the first one ON SCREEN wins, re-checked live. That
         * is how a step follows a control that is replaced by what it opens:
         * `['goal-compose', 'set-goal']` rings the button, then rings the
         * composer the moment the button becomes it.
         */
        anchor: AnchorNames;
      }
    | {
        kind: 'modal';
        media?: Media;
        input?: Input;
      }
    | {
        /** A pulsing dot that waits to be tapped — the quietest of the three,
         *  and the only one that interrupts nothing. Good for a feature that
         *  has just appeared and can wait to be discovered. */
        kind: 'beacon';
        anchor: AnchorNames;
      }
  );

export type Flow = {
  id: string;
  steps: Step[];
  /**
   * `onboarding` runs immediately and ignores the per-session budget: it is
   * the reason the app is open. `ambient` queues behind the budget, because
   * the student came to tick a habit and we are interrupting them.
   */
  priority: 'onboarding' | 'ambient';
};

/** Where flow progress is kept between sessions. */
export type CoachStore = {
  read(): Promise<CoachState>;
  write(state: CoachState): Promise<void>;
};

export type CoachState = {
  /** Flows that are finished, skipped, or abandoned. Never shown again. */
  seen: string[];
  /**
   * A flow in progress and how far it got.
   *
   * Resumability matters most for the flow that matters most: onboarding runs
   * on the live app, so someone will close it halfway through, and coming
   * back to step one — or to nothing — are both worse than continuing.
   */
  active?: { flowId: string; step: number };
  /** Answers collected by `input` steps, keyed by flow id. */
  answers?: Record<string, string>;
};

export const emptyCoachState: CoachState = { seen: [] };

/** How long to wait for an anchor that is not on screen yet. Tapping a button
 *  opens a panel whose fields did not exist a moment ago, so the common case
 *  is a short wait rather than a real absence. */
export const ANCHOR_TIMEOUT_MS = 4000;

/**
 * Steps allowed to vanish before the whole flow is abandoned.
 *
 * Flows rot: the UI moves and an anchor stops existing. One missing step is a
 * gap worth stepping over silently; several means this flow no longer
 * describes the app, and half a broken tour is worse than no tour. When that
 * happens the flow is marked seen so it never tries again.
 */
export const MAX_SKIPPED_STEPS = 2;
